/**
 * Filter dropdown widgets for the catalog table columns.
 *
 * Each column gets a small filter UI that writes a string-encoded value
 * to ``state.filters[columnKey]``. The workspace's article-row filter
 * pipeline parses the encoded value and applies the predicate. Encoding
 * scheme:
 *
 *   numeric range:   ``min..max``   (either side may be empty)
 *   multi-select:    ``a,b,c``
 *   toggle:          ``"on"`` (absent = off)
 *
 * This module intentionally co-locates the filter primitives
 * (encode/decode/apply helpers) with the dropdown components — they are
 * a cohesive unit and splitting them apart would just add ceremony. The
 * react-refresh fast-refresh warning is therefore disabled file-wide.
 */
/* eslint-disable react-refresh/only-export-components */

import { useState } from "react";
import { Button, Checkbox, Input, Space } from "antd";
import { NumInput as InputNumber } from "./NumInput";
import type { FilterDropdownProps } from "antd/es/table/interface";

// ---------------------------------------------------------------------------
// Encoders / decoders
// ---------------------------------------------------------------------------

export type NumericRange = { min: number | null; max: number | null };

export function decodeRange(raw: string | undefined): NumericRange {
  if (!raw) return { min: null, max: null };
  const [a, b] = raw.split("..");
  return {
    min: a ? Number(a) : null,
    max: b ? Number(b) : null,
  };
}

export function encodeRange(r: NumericRange): string {
  if (r.min === null && r.max === null) return "";
  return `${r.min ?? ""}..${r.max ?? ""}`;
}

export function decodeMulti(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(",").filter(Boolean);
}

export function encodeMulti(values: string[]): string {
  return values.join(",");
}

export function applyRange(
  value: number | null | undefined,
  range: NumericRange,
): boolean {
  if (range.min === null && range.max === null) return true;
  if (value === null || value === undefined) return false;
  if (range.min !== null && value < range.min) return false;
  if (range.max !== null && value > range.max) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Dropdown widgets
// ---------------------------------------------------------------------------

interface RangeProps {
  value: string | undefined;
  onApply: (next: string) => void;
  onClear: () => void;
  closeDropdown: () => void;
  unit?: string;
}

export function RangeFilterDropdown({
  value,
  onApply,
  onClear,
  closeDropdown,
  unit,
}: RangeProps) {
  const initial = decodeRange(value);
  const [min, setMin] = useState<number | null>(initial.min);
  const [max, setMax] = useState<number | null>(initial.max);
  return (
    <div style={{ padding: 8, minWidth: 220 }}>
      <Space direction="vertical" style={{ width: "100%" }} size={6}>
        <InputNumber
          placeholder={`Min${unit ? ` (${unit})` : ""}`}
          value={min ?? undefined}
          onChange={(v) => setMin(v as number | null)}
          style={{ width: "100%" }}
        />
        <InputNumber
          placeholder={`Max${unit ? ` (${unit})` : ""}`}
          value={max ?? undefined}
          onChange={(v) => setMax(v as number | null)}
          style={{ width: "100%" }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              onApply(encodeRange({ min, max }));
              closeDropdown();
            }}
          >
            Apply
          </Button>
          <Button
            size="small"
            onClick={() => {
              setMin(null);
              setMax(null);
              onClear();
              closeDropdown();
            }}
          >
            Clear
          </Button>
        </div>
      </Space>
    </div>
  );
}

interface MultiProps {
  value: string | undefined;
  options: { value: string; label: string }[];
  onApply: (next: string) => void;
  onClear: () => void;
  closeDropdown: () => void;
}

export function MultiFilterDropdown({
  value,
  options,
  onApply,
  onClear,
  closeDropdown,
}: MultiProps) {
  const [picked, setPicked] = useState<string[]>(decodeMulti(value));
  return (
    <div style={{ padding: 8, minWidth: 160 }}>
      <Checkbox.Group
        value={picked}
        onChange={(v) => setPicked(v as string[])}
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
        options={options}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            onApply(encodeMulti(picked));
            closeDropdown();
          }}
        >
          Apply
        </Button>
        <Button
          size="small"
          onClick={() => {
            setPicked([]);
            onClear();
            closeDropdown();
          }}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

interface ToggleProps {
  value: string | undefined;
  label: string;
  onApply: (next: string) => void;
  onClear: () => void;
  closeDropdown: () => void;
}

export function ToggleFilterDropdown({
  value,
  label,
  onApply,
  onClear,
  closeDropdown,
}: ToggleProps) {
  const [on, setOn] = useState(value === "on");
  return (
    <div style={{ padding: 8, minWidth: 160 }}>
      <Checkbox checked={on} onChange={(e) => setOn(e.target.checked)}>
        {label}
      </Checkbox>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            onApply(on ? "on" : "");
            closeDropdown();
          }}
        >
          Apply
        </Button>
        <Button
          size="small"
          onClick={() => {
            setOn(false);
            onClear();
            closeDropdown();
          }}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

interface TextProps {
  value: string | undefined;
  onApply: (next: string) => void;
  onClear: () => void;
  closeDropdown: () => void;
}

export function TextFilterDropdown({
  value,
  onApply,
  onClear,
  closeDropdown,
}: TextProps) {
  const [draft, setDraft] = useState(value ?? "");
  return (
    <div style={{ padding: 8, minWidth: 220 }}>
      <Input
        placeholder="contains…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPressEnter={() => {
          onApply(draft.trim());
          closeDropdown();
        }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            onApply(draft.trim());
            closeDropdown();
          }}
        >
          Apply
        </Button>
        <Button
          size="small"
          onClick={() => {
            setDraft("");
            onClear();
            closeDropdown();
          }}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

// Helper to wire a dropdown into an AntD filterDropdown prop.
export function bindFilter(
  current: string | undefined,
  setFilter: (raw: string) => void,
  clearFilter: () => void,
  Inner: (p: {
    value: string | undefined;
    onApply: (next: string) => void;
    onClear: () => void;
    closeDropdown: () => void;
  }) => JSX.Element,
) {
  return (props: FilterDropdownProps) => (
    <Inner
      value={current}
      onApply={setFilter}
      onClear={clearFilter}
      closeDropdown={() => props.confirm({ closeDropdown: true })}
    />
  );
}
